import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BarChart, LineChart, Users, Search, Star, MapPin, TrendingUp, MousePointer, Phone, Calendar, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

const getTimeframeDate = (timeframe: string): string => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  switch (timeframe) {
    case 'today':
      return now.toISOString();
    case 'yesterday':
      now.setDate(now.getDate() - 1);
      return now.toISOString();
    case 'this_week':
      now.setDate(now.getDate() - now.getDay());
      return now.toISOString();
    case 'this_month':
      now.setDate(1);
      return now.toISOString();
    case 'last_week':
      now.setDate(now.getDate() - 7);
      return now.toISOString();
    case 'last_month':
      now.setMonth(now.getMonth() - 1);
      return now.toISOString();
    case 'last_year':
      now.setFullYear(now.getFullYear() - 1);
      return now.toISOString();
    default:
      now.setMonth(now.getMonth() - 1);
      return now.toISOString();
  }
};

interface AnalyticsData {
  totalPlumbers: number;
  totalReviews: number;
  totalSearches: number;
  totalLeads: number;
  totalConversions: number;
  averageRating: number;
  topCities: { city: string; count: number }[];
  topServices: { service: string; count: number }[];
  searchTerms: {
    term: string;
    count: number;
    conversionRate: number;
  }[];
  leadsByType: {
    type: string;
    count: number;
  }[];
  conversionsByType: {
    type: string;
    count: number;
    value: number;
  }[];
  conversionData: {
    searches: number;
    profileViews: number;
    phoneClicks: number;
    registrations: number;
  };
}

export function Analytics() {
  const [data, setData] = useState<AnalyticsData>({
    totalPlumbers: 0,
    totalReviews: 0,
    totalSearches: 0,
    totalLeads: 0,
    totalConversions: 0,
    averageRating: 0,
    topCities: [],
    topServices: [],
    searchTerms: [],
    leadsByType: [],
    conversionsByType: [],
    conversionData: {
      searches: 0,
      profileViews: 0,
      phoneClicks: 0,
      registrations: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_week' | 'last_month' | 'last_year' | 'custom'>('this_month');
  const [view, setView] = useState<'overview' | 'conversions' | 'search' | 'leads'>('overview');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  });

  useEffect(() => {
    async function loadAnalytics() {
      try {
        const startDate = getTimeframeDate(timeframe);

        // Get services data
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('id, title');

        if (servicesError) throw servicesError;

        // Get cities data first
        const { data: citiesData, error: citiesError } = await supabase
          .from('cities')
          .select('name, zip_codes');

        if (citiesError) throw citiesError;

        // Get plumbers count
        const { data: plumbersData, error: plumbersError } = await supabase
          .from('plumbers')
          .select('id, service_areas:plumber_service_areas(zip_code)');

        if (plumbersError) throw plumbersError;

        // Get reviews data
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('rating, created_at')
          .gte('created_at', startDate)
          .order('created_at', { ascending: false });

        if (reviewsError) throw reviewsError;

        // Get searches data
        const { data: searchesData, error: searchesError } = await supabase
          .from('analytics_searches')
          .select(`
            id,
            query,
            zip_code,
            city_id,
            service_id,
            filters,
            results_count,
            metadata,
            created_at,
            city:city_id(
              name,
              state:state_id(code)
            )
          `)
          .gte('created_at', startDate)
          .order('created_at', { ascending: false });

        if (searchesError) throw searchesError;

        // Get leads data
        const { data: leadsData, error: leadsError } = await supabase
          .from('analytics_leads')
          .select(`
            id,
            type,
            city_id,
            service_id,
            plumber_id,
            metadata,
            created_at,
            plumber:plumber_id(business_name)
          `)
          .gte('created_at', startDate)
          .order('created_at', { ascending: false });

        if (leadsError) throw leadsError;

        // Get conversions data
        const { data: conversionsData, error: conversionsError } = await supabase
          .from('analytics_conversions')
          .select(`
            id,
            type,
            value,
            metadata,
            created_at,
            plumber:plumber_id(business_name)
          `)
          .gte('created_at', startDate)
          .order('created_at', { ascending: false });

        if (conversionsError) throw conversionsError;

        // Process the data
        const processedData = {
          plumbers: plumbersData || [],
          reviews: reviewsData || [],
          searches: searchesData || [],
          leads: leadsData?.map(lead => ({
            ...lead
          })) || [],
          conversions: conversionsData?.map(conv => ({
            ...conv
          })) || []
        };

        const avgRating = processedData.reviews.length > 0
          ? processedData.reviews.reduce((acc, rev) => acc + rev.rating, 0) / processedData.reviews.length
          : 0;

        setData({
          totalPlumbers: plumbersData?.length || 0,
          totalReviews: processedData.reviews.length,
          totalSearches: processedData.searches.length,
          totalLeads: leadsData?.length || 0,
          totalConversions: conversionsData?.length || 0,
          averageRating: avgRating,
          topCities: processTopCities(processedData.plumbers, citiesData || []),
          topServices: processTopServices(processedData.searches, servicesData || []),
          searchTerms: processSearchTerms(processedData.searches),
          leadsByType: processLeadsByType(processedData.leads),
          conversionsByType: processConversionsByType(processedData.conversions),
          conversionData: {
            searches: processedData.searches.length,
            profileViews: processedData.leads.filter(l => l.type === 'profile_view').length,
            phoneClicks: processedData.leads.filter(l => l.type === 'phone_click').length,
            registrations: processedData.leads.filter(l => l.type === 'registration').length
          }
        });
      } catch (error) {
        console.error('Error loading analytics:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    }

    loadAnalytics();
  }, [timeframe]);

  const processAnalyticsData = (
    plumbers: any[],
    reviews: any[],
    services: any[],
    leads: any[],
    conversions: any[],
    searches: any[]
  ) => {
    return {
      searches: searches.length,
      leads,
      conversions,
      conversionData: {
        searches: searches.length,
        profileViews: conversions.filter(c => c.type === 'profile_view').length,
        phoneClicks: conversions.filter(c => c.type === 'phone_click').length,
        registrations: conversions.filter(c => c.type === 'registration').length
      }
    };
  };

  const processSearchTerms = (searches: any[]) => {
    const terms = searches.reduce((acc: any, search: any) => {
      if (!acc[search.zip_code]) {
        acc[search.zip_code] = {
          count: 0,
          conversions: 0
        };
      }
      acc[search.zip_code].count++;
      if (search.metadata?.conversion_type) {
        acc[search.zip_code].conversions++;
      }
      return acc;
    }, {});

    return Object.entries(terms).map(([term, data]: [string, any]) => ({
      term,
      count: data.count,
      conversionRate: data.conversions / data.count
    }));
  };

  const processTopServices = (searches: any[], services: any[]) => {
    // Create a map of service IDs to names
    const serviceMap = new Map(services.map(s => [s.id, s.title]));
    
    // Count searches by service
    const serviceCounts = searches.reduce((acc: Record<string, number>, search: any) => {
      if (search.service_id && serviceMap.has(search.service_id)) {
        const serviceName = serviceMap.get(search.service_id)!;
        acc[serviceName] = (acc[serviceName] || 0) + 1;
      }
      return acc;
    }, {});

    // Convert to array and sort by count
    return Object.entries(serviceCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const processLeadsByType = (leads: any[]) => {
    return Object.entries(
      leads.reduce((acc: any, lead: any) => {
        const type = lead.type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {})
    ).map(([type, count]) => ({ type, count }));
  };

  const processConversionsByType = (conversions: any[]) => {
    return Object.entries(
      conversions.reduce((acc: any, conv: any) => {
        const type = conv.type || 'unknown';
        if (!acc[type]) {
          acc[type] = { count: 0, value: 0 };
        }
        acc[type].count++;
        acc[type].value += parseFloat(conv.value) || 0;
        return acc;
      }, {})
    ).map(([type, data]: [string, any]) => ({ type, ...data }));
  };

  const processTopCities = (plumbers: any[], cities: any[]) => {
    // Create a map of zip codes to city names
    const zipToCityMap = new Map();
    cities.forEach((city: any) => {
      city.zip_codes?.forEach((zip: string) => {
        zipToCityMap.set(zip, city.name);
      });
    });

    // Count plumbers per city using the zip code mapping
    const cityCounts = plumbers.reduce((acc: Record<string, number>, plumber: any) => {
      plumber.service_areas?.forEach((area: any) => {
        const cityName = zipToCityMap.get(area.zip_code);
        if (cityName) {
          acc[cityName] = (acc[cityName] || 0) + 1;
        }
      });
      return acc;
    }, {});

     return Object.entries(cityCounts)
       .map(([name, count]) => ({ name, count }))
       .sort((a, b) => b.count - a.count)
       .slice(0, 5);
   };

  const renderConversionMetrics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Conversion Funnel</h3>
      <div className="relative pt-1">
        {Object.entries(data.conversionData).map(([key, value], index) => (
          <div key={key} className="mb-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </div>
              <div className="text-sm font-medium text-gray-500">{value}</div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full">
              <div
                className="bg-blue-600 rounded-full h-2"
                style={{
                  width: `${(value / data.conversionData.searches) * 100}%`
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSearchAnalytics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Top Search Terms</h3>
      {data.searchTerms.map(term => (
        <div key={term.term} className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-900">{term.term}</span>
            <span className="text-sm text-gray-500">{term.count} searches</span>
          </div>
          <div className="mt-2">
            <div className="text-sm text-gray-600">
              Conversion Rate: {(term.conversionRate * 100).toFixed(1)}%
            </div>
            <div className="mt-1 h-2 bg-gray-100 rounded-full">
              <div
                className="h-2 bg-green-600 rounded-full"
                style={{ width: `${term.conversionRate * 100}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderLeadsAnalytics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Lead Analytics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-medium mb-4">Leads by Type</h4>
          <div className="space-y-4">
            {data.leadsByType.map(lead => (
              <div key={lead.type} className="flex justify-between items-center">
                <span className="text-gray-700">{lead.type}</span>
                <span className="font-medium">{lead.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-lg font-medium mb-4">Lead Status Distribution</h4>
          <div className="space-y-4">
            {data.conversionsByType.map(conv => (
              <div key={conv.type} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">{conv.type}</span>
                  <span className="font-medium">{conv.count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 bg-blue-600 rounded-full"
                    style={{
                      width: `${(conv.count / data.totalLeads) * 100}%`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h4 className="text-lg font-medium mb-4">Recent Leads</h4>
        <div className="text-sm text-gray-500">
          View and manage leads in the{' '}
          <Link to="/admin/leads" className="text-blue-600 hover:underline">Leads Management</Link> section
        </div>
      </div>
    </div>
  );

  if (loading) return <div>Loading analytics...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
        <div className="flex space-x-4">
          <select 
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as any)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="last_week">Last Week</option>
            <option value="last_month">Last Month</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Range</option>
          </select>
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setView('overview')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'overview'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border rounded-l-md`}
            >
              Overview
            </button>
            <button
              onClick={() => setView('conversions')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'conversions'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border-t border-b border-r`}
            >
              Conversions
            </button>
            <button
              onClick={() => setView('search')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'search'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border-t border-b border-r`}
            >
              Search
            </button>
            <button
              onClick={() => setView('leads')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'leads'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border-t border-b border-r rounded-r-md`}
            >
              Leads
            </button>
          </div>
        </div>
      </div>

      {view === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Plumbers</p>
                  <p className="text-2xl font-bold text-gray-900">{data.totalPlumbers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Reviews</p>
                  <p className="text-2xl font-bold text-gray-900">{data.totalReviews}</p>
                </div>
                <Star className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Searches</p>
                  <p className="text-2xl font-bold text-gray-900">{data.totalSearches}</p>
                </div>
                <Search className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-900">{data.totalLeads}</p>
                </div>
                <Phone className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Conversions</p>
                  <p className="text-2xl font-bold text-gray-900">{data.totalConversions}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Average Rating</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {data.averageRating.toFixed(1)}
                  </p>
                </div>
                <Star className="w-8 h-8 text-yellow-500 fill-current" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Cities</h3>
              <div className="space-y-4">
                {data.topCities.map((city: any) => (
                  <div key={city.name} className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-2" />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{city.name}</span>
                        <span className="text-gray-500">{city.count} plumbers</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-100 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{
                            width: `${(city.count / data.totalPlumbers) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Services</h3>
              <div className="space-y-4">
                {data.topServices.map((service: any) => (
                  <div key={service.name} className="flex items-center">
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{service.name}</span>
                        <span className="text-gray-500">{service.count} providers</span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-100 rounded-full">
                        <div
                          className="h-2 bg-green-600 rounded-full"
                          style={{
                            width: `${(service.count / data.totalPlumbers) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {view === 'conversions' && renderConversionMetrics()}
      {view === 'search' && renderSearchAnalytics()}
      {view === 'leads' && renderLeadsAnalytics()}
    </div>
  );
}